
import { useMemo } from "react";
import { DiaryEntry } from "@/utils/diary/types";
import { cmToInches, gToLb } from "@/utils/familyProfile";
import { format } from "date-fns";
import { extractMilkIntakeValue } from "@/components/statistics/chart/data/MilkIntakeData";

export function useGrowthData(
  entries: DiaryEntry[],
  useMetricHeight: boolean,
  useMetricWeight: boolean
) {
  const heightData = useMemo(() => {
    if (!entries || entries.length === 0) return [];

    // Process entries with height data
    return entries
      .filter(entry => entry.height)
      .map(entry => {
        // Parse height value from string (e.g., "60 cm" -> 60)
        const heightMatch = entry.height?.match(/(\d+(\.\d+)?)/);
        const heightValue = heightMatch ? parseFloat(heightMatch[0]) : null;
        
        // Skip entries without valid height data
        if (!heightValue) return null;
        
        // Check if units are specified
        const isMetricHeight = /cm|centimeter|centimetre/i.test(entry.height || "");
        const isImperialHeight = /in|inch|inches/i.test(entry.height || "");
        
        // If no specific unit mentioned, we'll assume it's in cm
        const originalValue = isImperialHeight ? heightValue * 2.54 : heightValue;
        
        // Calculate display value based on user preferences
        const displayValue = useMetricHeight
          ? originalValue
          : cmToInches(originalValue);

        return {
          date: entry.date || entry.createdAt,
          formattedDate: format(new Date(entry.date || entry.createdAt), "yyyy-MM-dd"),
          originalHeight: originalValue,
          displayHeight: displayValue
        };
      })
      .filter(Boolean) // Remove null entries
      .sort((a, b) => new Date(a!.date).getTime() - new Date(b!.date).getTime());
  }, [entries, useMetricHeight]);

  const weightData = useMemo(() => {
    if (!entries || entries.length === 0) return [];
    
    // Process entries with weight data
    return entries
      .filter(entry => entry.weight)
      .map(entry => {
        // Parse weight value from string (e.g., "3.5 kg" -> 3.5)
        const weightMatch = entry.weight?.match(/(\d+(\.\d+)?)/);
        const weightValue = weightMatch ? parseFloat(weightMatch[0]) : null;
        
        // Skip entries without valid weight data
        if (!weightValue) return null;
        
        // Check if units are specified
        const isMetricWeight = /kg|kilo|kilogram/i.test(entry.weight || "");
        const isImperialWeight = /lb|pound|pounds/i.test(entry.weight || "");
        
        // Convert to grams for standardized storage
        let originalValue: number;
        if (isMetricWeight) {
          originalValue = weightValue * 1000; // kg to g
        } else if (isImperialWeight) {
          originalValue = weightValue * 453.592; // lb to g
        } else {
          // If no specific unit mentioned, we'll assume grams
          originalValue = weightValue;
        }
        
        // Calculate display value based on user preferences
        const displayValue = useMetricWeight
          ? originalValue / 1000 // Display in kg
          : gToLb(originalValue);

        return {
          date: entry.date || entry.createdAt,
          formattedDate: format(new Date(entry.date || entry.createdAt), "yyyy-MM-dd"),
          originalWeight: originalValue,
          displayWeight: displayValue
        };
      })
      .filter(Boolean) // Remove null entries
      .sort((a, b) => new Date(a!.date).getTime() - new Date(b!.date).getTime());
  }, [entries, useMetricWeight]);

  // NEW: Process milk intake data
  const milkData = useMemo(() => {
    if (!entries || entries.length === 0) return [];
    
    // Process entries with milk intake data
    return entries
      .filter(entry => entry.milkIntake)
      .map(entry => {
        // Extract milk intake value
        const milkValue = extractMilkIntakeValue(entry.milkIntake);
        
        // Skip entries without valid milk data
        if (milkValue === null) return null;
        
        // Store in ml (metric) and convert for display if needed
        const originalValue = milkValue;
        
        // Calculate display value based on user preferences (ml or oz)
        const displayValue = useMetricWeight
          ? originalValue
          : originalValue * 0.033814; // ml to oz

        return {
          date: entry.date || entry.createdAt,
          formattedDate: format(new Date(entry.date || entry.createdAt), "yyyy-MM-dd"),
          originalMilkIntake: originalValue,
          displayMilkIntake: displayValue
        };
      })
      .filter(Boolean) // Remove null entries
      .sort((a, b) => new Date(a!.date).getTime() - new Date(b!.date).getTime());
  }, [entries, useMetricWeight]);

  return { heightData, weightData, milkData };
}
